import React, { useEffect, useRef, useState } from 'react';
import {
  Avatar,
  Chip,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Tooltip,
  Snippet,
  Button,
} from '@heroui/react';
import { ResponsiveTooltip, Typography } from '../../../../components';
import { InfoCircle, LinkIcon } from '../../../../components/icons';
import { PartnerOrderData } from '../types';
import { useTranslations } from '../../../../contexts/translation.context';
import { Icon } from '@iconify/react/dist/iconify.js';
import BlockedCountriesTooltip from './BlockedCountriesTooltip';
import OrderInfoTooltip from './OrderInfoTooltip';

interface ModelCellProps {
  order: PartnerOrderData;
  onShowContent: (order: PartnerOrderData) => void;
}

const ModelCell: React.FC<ModelCellProps> = ({ order, onShowContent }) => {
  const { t } = useTranslations();

  // Расчет процента конверсии (подписавшиеся / переходы по ссылке)
  const transitionsCount = order.campaign.transitions_count || 0;
  const subscribedCount = order.statistics.subscribedCount || 0;
  const conversionRate =
    transitionsCount > 0 ? ((subscribedCount / transitionsCount) * 100).toFixed(2) : '0.00';
  const offerShortId = order.offer?.id ? order.offer.id.slice(-4).toUpperCase() : '----';
  const [isBlockedCountriesOpen, setIsBlockedCountriesOpen] = useState(false);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const openBlockedCountries = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setIsBlockedCountriesOpen(true);
  };

  const scheduleBlockedCountriesClose = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setIsBlockedCountriesOpen(false);
    }, 120);
  };

  return (
    <div className="flex items-start gap-3">
      <div className="relative">
        <Avatar
          src={order.model.avatar}
          showFallback
          name={order.model.name?.slice(0, 1)}
          className="w-10 h-10 rounded-3"
        />
      </div>
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <Typography variant="label2" className="leading-5">
            {order.model.link?.split('/').reverse()?.[0]?.length > 5
              ? order.model.link?.split('/').reverse()?.[0]?.slice(-4)
              : order.model.link?.split('/').reverse()?.[0] || 'Unknown'}
          </Typography>
          <div className="flex gap-2">
            <a
              target="__blank"
              href={`https://onlyfans.com/${order.model.username}`}
              className="text-blue-600 hover:underline flex items-center gap-1"
            >
              <Typography variant="body2" className="!text-partner">
                @{order.model.username}
              </Typography>
            </a>
          </div>
          <ResponsiveTooltip content={t('partner.orders.subscriptionTypeTooltip')} placement="top">
            <Chip
              size="sm"
              variant="flat"
              classNames={{
                base: ' bg-partner/20',
                content: 'text-partner',
              }}
            >
              {order.model.type === 'free' ? 'FREE' : 'Free-trial'}
            </Chip>
          </ResponsiveTooltip>
        </div>
        <div className="flex flex-wrap gap-0.5">
          <ResponsiveTooltip content={t('partner.orders.offerIdTooltip')} placement="top">
            <Typography variant="body2" className="text-auth-muted mr-0.5">
              #{order.offer.id.slice(-4)}
            </Typography>
          </ResponsiveTooltip>
          <Typography variant="body2" className="text-auth-muted">
            {order.offer.platform}
          </Typography>
          <ResponsiveTooltip
            placement="top"
            offset={10}
            showArrow
            content={<OrderInfoTooltip order={order} />}
            classNames={{
              base: 'max-w-none',
              content: 'bg-white p-4 rounded-2xl border border-tin shadow-xl',
            }}
          >
            <div className="cursor-pointer">
              <InfoCircle size={16} />
            </div>
          </ResponsiveTooltip>
        </div>
        {order.blocked_countries && order.blocked_countries.length > 0 && (
          <Popover
            placement="bottom-start"
            offset={12}
            showArrow
            isOpen={isBlockedCountriesOpen}
            onOpenChange={setIsBlockedCountriesOpen}
            shouldCloseOnBlur
            isDismissable
          >
            <PopoverTrigger>
              <div
                className="cursor-pointer"
                onMouseEnter={openBlockedCountries}
                onMouseLeave={scheduleBlockedCountriesClose}
              >
                <Typography variant="body2" className="text-auth-muted text-xs">
                  {t('partner.orders.blockedCountries')}: {order.blocked_countries.length}
                </Typography>
              </div>
            </PopoverTrigger>
            <PopoverContent
              className="p-0 bg-white border border-tin rounded-2xl shadow-xl"
              onMouseEnter={openBlockedCountries}
              onMouseLeave={scheduleBlockedCountriesClose}
            >
              <BlockedCountriesTooltip
                countries={order.blocked_countries}
                offerShortId={offerShortId}
                modelUsername={order.model.username}
                profileUrl={`https://onlyfans.com/${order.model.username}`}
              />
            </PopoverContent>
          </Popover>
        )}
        <div className="flex items-center gap-1">
          <Snippet
            symbol={null}
            codeString={order.campaign.link}
            className="bg-husky rounded-full px-2 py-[0.1875rem]"
            classNames={{
              base: 'bg-husky rounded-full px-2 py-[0.1875rem] flex items-center gap-1 shadow-none',
              pre: 'bg-transparent p-0 flex items-center gap-1 max-w-[13.75rem]',
              copyButton: 'text-partner min-w-5 h-5',
              checkIcon: 'text-partner',
              content: 'flex items-center gap-1 max-w-[13.75rem]',
            }}
          >
            <a
              href={order.campaign.link}
              target="__blank"
              className="flex items-center gap-1 no-underline"
            >
              <LinkIcon size={18} fill="#006FEE" className="text-partner" />
              <Typography variant="body2" className="!text-partner truncate max-w-[11.25rem]">
                {order.campaign.link}
              </Typography>
            </a>
          </Snippet>
          <div className="flex items-center gap-1">
            <ResponsiveTooltip content={t('partner.orders.guestsTooltip')} placement="top">
              <div className="flex items-center gap-1">
                <Icon icon="solar:eye-bold" width={16} height={16} className="text-default-400" />
                <span className="text-xs text-default-500">{transitionsCount}</span>
              </div>
            </ResponsiveTooltip>
            <ResponsiveTooltip content={t('partner.orders.conversionTooltip')} placement="top">
              <span className="text-xs text-default-600">({conversionRate}%)</span>
            </ResponsiveTooltip>
          </div>
        </div>

        {/* Show Content Button */}
        <Button
          size="sm"
          className="bg-[#E6F1FE] text-[#006FEE] h-7 rounded-2 px-3 text-xs font-normal mt-1 w-fit"
          onPress={() => onShowContent(order)}
        >
          {t('partner.orders.showContent')}
        </Button>
      </div>
    </div>
  );
};

export default ModelCell;
